import React, { useState, useEffect } from 'react';
import {
  VStack,
  HStack,
  Text,
  Badge,
  Icon,
  Box,
  Skeleton,
  Button,
  Divider,
  useColorModeValue,
  Tooltip,
  Avatar,
  Flex,
  SimpleGrid,
  useDisclosure,
  useToast,
  Select,
  Fade,
  ScaleFade,
  SlideFade,
  keyframes,
  usePrefersReducedMotion,
  Spinner
} from '@chakra-ui/react';
import { 
  FiDollarSign, 
  FiCalendar, 
  FiUser,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiRefreshCw,
  FiMoreHorizontal,
  FiEye,
  FiClipboard,
  FiExternalLink
} from 'react-icons/fi';
import { useHistory } from 'react-router-dom';

// Service para obtener timeline unificado
import { getUnifiedActivityTimeline } from 'services/dashboardService';

// Hook para obtener información del usuario
import { useUserAuth, formatCurrency } from 'utils/site';

// Modal de detalles de pago (patrón exacto de Billing/Profile)
import ModalOrderEdit from 'components/Modal/ModalOrderEdit';
// Modal de detalles de actividad con navegación a perfil
import ModalActivityDetail from 'components/Modal/ModalActivityDetail';

// Animaciones personalizadas
const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 5px rgba(66, 153, 225, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(66, 153, 225, 0.6);
  }
`;

const slideInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const ActivityIcon = ({ type, status }) => {
  const getIconConfig = () => {
    switch (type) {
      case 'payment':
        return {
          icon: FiDollarSign,
          color: status === 'completed' ? 'green' : status === 'pending' ? 'orange' : 'red'
        };
      case 'event':
        return {
          icon: FiCalendar,
          color: 'purple'
        };
      case 'user':
        return {
          icon: FiUser,
          color: 'blue'
        };
      case 'survey':
        return {
          icon: FiClipboard,
          color: 'teal'
        };
      default:
        return {
          icon: FiClock,
          color: 'gray'
        };
    }
  };

  const config = getIconConfig();
  const bgColor = useColorModeValue(`${config.color}.100`, `${config.color}.900`);
  const iconColor = useColorModeValue(`${config.color}.600`, `${config.color}.300`);

  return (
    <ScaleFade in={true} initialScale={0.8}>
      <Box
        bg={bgColor}
        color={iconColor}
        p={2}
        borderRadius="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        transition="all 0.3s ease"
        _hover={{
          transform: 'scale(1.2)',
          animation: `${pulseGlow} 1.5s ease-in-out infinite`
        }}
      >
        <Icon as={config.icon} boxSize={3} />
      </Box>
    </ScaleFade>
  );
};

const StatusBadge = ({ status, type }) => {
  const getStatusConfig = () => {
    if (type === 'payment') {
      switch (status) {
        case 'completed':
          return { color: 'green', text: 'Pagado' };
        case 'pending':
          return { color: 'orange', text: 'Pendiente' };
        case 'failed':
          return { color: 'red', text: 'Fallido' };
        default:
          return { color: 'gray', text: 'Desconocido' };
      }
    } else if (type === 'event') {
      switch (status) {
        case 'scheduled':
          return { color: 'blue', text: 'Programado' };
        case 'completed':
          return { color: 'green', text: 'Completado' };
        case 'cancelled':
          return { color: 'red', text: 'Cancelado' };
        default:
          return { color: 'gray', text: 'Desconocido' };
      }
    } else if (type === 'survey') {
      switch (status) {
        case 'active':
          return { color: 'teal', text: 'Disponible' };
        case 'completed':
          return { color: 'green', text: 'Completada' };
        case 'closed':
          return { color: 'red', text: 'Cerrada' };
        default:
          return { color: 'gray', text: 'Inactiva' };
      }
    }
    return { color: 'gray', text: status };
  };

  const config = getStatusConfig();
  
  return (
    <Badge 
      colorScheme={config.color} 
      variant="subtle" 
      borderRadius="full"
      fontSize="xs"
    >
      {config.text}
    </Badge>
  );
};

const TimelineItem = ({ item, onPaymentClick, onActivityClick, index = 0 }) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('white', 'gray.700');
  const history = useHistory();
  const toast = useToast();
  const prefersReducedMotion = usePrefersReducedMotion();

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return `Hace ${diffMins} min`;
    } else if (diffHours < 24) {
      return `Hace ${diffHours}h`;
    } else if (diffDays < 7) {
      return `Hace ${diffDays}d`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Función para extraer user_id de la actividad según el tipo (para logging solamente)
  const getUserIdFromActivity = (item) => {
    switch (item.type) {
      case 'user':
        return item.data?.id;
      case 'payment':
        return item.data?.customer_id;
      case 'survey':
        return item.data?.respondent?.id;
      case 'event':
        return item.data?.created_by;
      default:
        return item.data?.user_id || item.data?.customer_id || item.data?.created_by;
    }
  };

  const handleItemClick = () => {
    // Siempre abrir el modal de detalles de actividad
    if (onActivityClick) {
      onActivityClick(item);
    }
  };

  const navigateToProfile = (e) => {
    e.stopPropagation();
    // Abrir el modal de detalles de actividad
    if (onActivityClick) {
      onActivityClick(item);
    }
  };

  return (
    <SlideFade
      in={true}
      offsetY="20px"
      delay={prefersReducedMotion ? 0 : index * 0.1}
    >
      <Box
        p={5}
        border="1px"
        borderColor={borderColor}
        borderRadius="xl"
        bg={bgColor}
        position="relative"
        overflow="hidden"
        _hover={{ 
          borderColor: 'blue.400', 
          cursor: 'pointer', 
          shadow: 'lg',
          transform: prefersReducedMotion ? 'none' : 'translateY(-3px) scale(1.02)',
          '&::before': {
            opacity: 1
          }
        }}
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(90deg, transparent, ${useColorModeValue('rgba(66, 153, 225, 0.05)', 'rgba(66, 153, 225, 0.1)')}, transparent)`,
          opacity: 0,
          transition: 'all 0.3s ease',
          borderRadius: 'inherit'
        }}
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        onClick={handleItemClick}
        w="100%"
        minW="320px"
        animation={prefersReducedMotion ? 'none' : `${slideInFromRight} 0.6s ease-out ${index * 0.1}s both`}
      >
      <VStack spacing={3} align="stretch">
        {/* Header con ícono, título y estado */}
        <Flex justify="space-between" align="start">
          <HStack spacing={3} flex={1}>
            <ActivityIcon type={item.type} status={item.status} />
            <VStack align="start" spacing={0} flex={1}>
              <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                {item.title}
              </Text>
              <Text fontSize="xs" color="gray.500" noOfLines={1}>
                {formatTimestamp(item.timestamp)}
              </Text>
            </VStack>
          </HStack>
          
          <HStack spacing={2}>
            <StatusBadge status={item.status} type={item.type} />
            {/* Botón para ver detalles de la actividad */}
            <Tooltip label="Ver detalles de la actividad">
              <Button
                size="xs"
                variant="ghost"
                colorScheme="blue"
                onClick={navigateToProfile}
              >
                <Icon as={FiExternalLink} />
              </Button>
            </Tooltip>
            {/* Botón específico para ver detalles del pago */}
            {item.type === 'payment' && (
              <Tooltip label="Ver detalles del pago">
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="green"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPaymentClick(item.data);
                  }}
                >
                  <Icon as={FiEye} />
                </Button>
              </Tooltip>
            )}
          </HStack>
        </Flex>
        
        {/* Descripción */}
        <Text fontSize="xs" color="gray.600" noOfLines={2}>
          {item.description}
        </Text>
        
        {/* Detalles específicos según tipo */}
        {item.type === 'payment' && (
          <VStack spacing={3} align="stretch" bg="green.50" p={4} borderRadius="md">
            <HStack justify="space-between">
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" color="green.600" fontWeight="semibold">
                  💳 Cliente
                </Text>
                <Text fontSize="xs" color="gray.700">
                  {item.data?.billing?.first_name} {item.data?.billing?.last_name}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {item.data?.billing?.email}
                </Text>
              </VStack>
              <VStack align="end" spacing={0}>
                <Text fontSize="sm" color="green.700" fontWeight="bold">
                  ${parseFloat(item.data?.total || 0).toLocaleString()}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {item.data?.currency || 'CLP'}
                </Text>
              </VStack>
            </HStack>
            <HStack justify="space-between" fontSize="xs" color="gray.600">
              <Text>🏦 Método: {item.data?.payment_method_title || 'Manual'}</Text>
              <Text>🏷️ #{item.data?.id || item.id}</Text>
            </HStack>
          </VStack>
        )}
        
        {item.type === 'user' && (
          <VStack spacing={3} align="stretch" bg={item.data?.action === 'updated' ? 'orange.50' : 'blue.50'} p={4} borderRadius="md">
            <HStack justify="space-between">
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" color={item.data?.action === 'updated' ? 'orange.600' : 'blue.600'} fontWeight="semibold">
                  {item.data?.action === 'updated' ? '👤 Usuario Actualizado' : '👤 Nuevo Usuario'}
                </Text>
                <Text fontSize="xs" color="gray.700">
                  {item.data?.first_name} {item.data?.last_name}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {item.data?.email}
                </Text>
              </VStack>
              <VStack align="end" spacing={0}>
                <Text fontSize="xs" color={item.data?.action === 'updated' ? 'orange.600' : 'blue.600'} fontWeight="medium">
                  {item.data?.role || 'Cliente'}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  RUT: {item.data?.username}
                </Text>
              </VStack>
            </HStack>
            <HStack justify="space-between" fontSize="xs" color="gray.600">
              <Text>🏢 Grupo: {item.data?.group || 'Sin asignar'}</Text>
              <Text>🏷️ #{item.data?.id || item.id}</Text>
            </HStack>
          </VStack>
        )}
        
        {item.type === 'event' && (() => {
          const categoryLabels = {
            clase: 'Clase', reunion: 'Reunión', evaluacion: 'Evaluación',
            actividad: 'Actividad', importante: 'Importante', cita: 'Cita', otro: 'Evento'
          };
          const startDate = item.data?.start_date ? new Date(item.data.start_date) : null;
          const endDate   = item.data?.end_date   ? new Date(item.data.end_date)   : null;
          const isAllDay  = item.data?.all_day == 1 || item.data?.all_day === true;
          const timeStr   = startDate && !isAllDay
            ? startDate.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: true })
            : 'Todo el día';
          const endTimeStr = endDate && !isAllDay
            ? endDate.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: true })
            : null;
          return (
            <VStack spacing={2} align="stretch" bg="purple.50" p={3} borderRadius="md">
              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontSize="xs" color="purple.600" fontWeight="semibold">
                    📅 {categoryLabels[item.data?.category] || 'Evento'}
                  </Text>
                  {item.data?.location && (
                    <Text fontSize="xs" color="gray.600">📍 {item.data.location}</Text>
                  )}
                </VStack>
                <VStack align="end" spacing={0}>
                  <Text fontSize="xs" color="purple.600" fontWeight="medium">{timeStr}</Text>
                  {endTimeStr && <Text fontSize="xs" color="gray.500">hasta {endTimeStr}</Text>}
                </VStack>
              </HStack>
              <HStack justify="space-between" fontSize="xs" color="gray.600">
                <Text>📋 {startDate ? startDate.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</Text>
                <Text>🏷️ #{item.data?.id || item.id}</Text>
              </HStack>
            </VStack>
          );
        })()}
        
        {item.type === 'survey' && (
          <VStack spacing={3} align="stretch" bg="teal.50" p={4} borderRadius="md">
            <HStack justify="space-between">
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" color="teal.600" fontWeight="semibold">
                  📝 {item.data?.survey?.name || 'Formulario'}
                </Text>
                <Text fontSize="xs" color="gray.700">
                  {item.data?.student_name 
                    ? `Estudiante: ${item.data.student_name}` 
                    : `Por: ${item.data?.respondent_full_name || 'Usuario anónimo'}`}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {item.data?.course_name 
                    ? `📚 ${item.data.course_name}` 
                    : (item.data?.survey_category 
                      ? `📋 ${item.data.survey_category}` 
                      : 'Sin categoría')}
                </Text>
              </VStack>
              <VStack align="end" spacing={0}>
                <Text fontSize="xs" color="teal.600" fontWeight="medium">
                  Completado
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {new Date(item.data?.completion_date || item.timestamp).toLocaleTimeString()}
                </Text>
              </VStack>
            </HStack>
            <HStack justify="space-between" fontSize="xs" color="gray.600">
              <Text>📅 {new Date(item.data?.completion_date || item.data?.created_at || item.timestamp).toLocaleDateString()}</Text>
              <Text>🏷️ #{item.data?.id || item.id}</Text>
            </HStack>
          </VStack>
        )}
        
        {/* Fallback para otros tipos */}
        {!['payment', 'user', 'event', 'survey'].includes(item.type) && (
          <HStack justify="space-between" fontSize="xs" color="gray.500">
            <Text>📄 Tipo: {item.type}</Text>
            <Text>🏷️ #{item.data?.id || item.id}</Text>
          </HStack>
        )}
      </VStack>
    </Box>
    </SlideFade>
  );
};

const ActivitySummary = ({ activities, isTeacher = false }) => {
  const summary = activities.reduce((acc, item) => {
    acc[item.type] = (acc[item.type] || 0) + 1;
    return acc;
  }, {});

  // Filtrar items del resumen si es profesor (omitir pagos)
  const allSummaryItems = [
    { 
      type: 'payment', 
      count: summary.payment || 0, 
      label: 'Pagos',
      icon: FiDollarSign,
      color: 'green'
    },
    { 
      type: 'event', 
      count: summary.event || 0, 
      label: 'Eventos',
      icon: FiCalendar,
      color: 'purple'
    },
    { 
      type: 'user', 
      count: summary.user || 0, 
      label: 'Usuarios',
      icon: FiUser,
      color: 'blue'
    },
    { 
      type: 'survey', 
      count: summary.survey || 0, 
      label: 'Formularios',
      icon: FiClipboard,
      color: 'teal'
    }
  ];

  // Si es profesor, omitir el item de pagos
  const summaryItems = isTeacher ? 
    allSummaryItems.filter(item => item.type !== 'payment') : 
    allSummaryItems;

  return (
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={4}>
      {summaryItems.map(item => (
        <Box
          key={item.type}
          p={3}
          bg={`${item.color}.50`}
          borderRadius="md"
          textAlign="center"
        >
          <VStack spacing={1}>
            <Icon as={item.icon} color={`${item.color}.500`} boxSize={4} />
            <Text fontSize="lg" fontWeight="bold" color={`${item.color}.600`}>
              {item.count}
            </Text>
            <Text fontSize="xs" color={`${item.color}.500`}>
              {item.label}
            </Text>
          </VStack>
        </Box>
      ))}
    </SimpleGrid>
  );
};

// Helpers y fila compacta (patrón .act del rediseño Home)
const formatRelativeTime = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return `hace ${diffMins} min`;
  if (diffHours < 24) return `hace ${diffHours}h`;
  if (diffDays < 7) return `hace ${diffDays}d`;
  return date.toLocaleDateString('es-CL');
};

const ACTIVITY_ICONS = {
  payment: FiDollarSign,
  user: FiUser,
  event: FiCalendar,
  survey: FiClipboard
};

const getActivityPalette = (item) => {
  if (item.type === 'payment') {
    if (item.status === 'completed') return 'mint';
    if (item.status === 'failed') return 'rose';
    return 'coral';
  }
  if (item.type === 'survey') return 'lav';
  if (item.type === 'event') return 'butter';
  return 'sky';
};

const renderActivityTitle = (title) => {
  const words = (title || '').trim().split(/\s+/);
  if (words.length < 2) return title;
  const lastWord = words[words.length - 1];
  const rest = words.slice(0, -1).join(' ');
  return (
    <>
      {rest}{' '}
      <Text as="b" fontWeight="800">{lastWord}</Text>
    </>
  );
};

const CompactActivityRow = ({ item, onClick }) => {
  const palette = getActivityPalette(item);
  const bg = palette === 'coral' ? 'coral.soft' : `${palette}.bg`;
  const ink = palette === 'coral' ? 'coral.700' : `${palette}.ink`;
  const ActivityIconCmp = ACTIVITY_ICONS[item.type] || FiClock;
  const amount = item.type === 'payment'
    ? formatCurrency(parseFloat(item.data?.total || 0))
    : null;

  return (
    <Flex
      gap="12px"
      p="13px 4px"
      borderTop="1px solid"
      borderColor="cream.line"
      align="flex-start"
      cursor="pointer"
      onClick={onClick}
      _hover={{ bg: 'cream.bg' }}
      borderRadius="10px"
      transition="background 0.15s"
    >
      <Flex w="36px" h="36px" borderRadius="11px" bg={bg} color={ink} align="center" justify="center" flexShrink={0}>
        <Icon as={ActivityIconCmp} boxSize="16px" />
      </Flex>
      <Box flex="1" minW={0}>
        <Text fontSize="13.5px" lineHeight="1.3" noOfLines={1}>
          {renderActivityTitle(item.title)}
        </Text>
        <Text fontSize="12px" color="ink.400" fontWeight="600" mt="3px" noOfLines={1}>
          {item.description} · {formatRelativeTime(item.timestamp)}
        </Text>
      </Box>
      {amount && (
        <Text
          fontSize="14px"
          fontWeight="800"
          whiteSpace="nowrap"
          color={item.status === 'completed' ? 'ok.500' : 'ink.900'}
        >
          {amount}
        </Text>
      )}
    </Flex>
  );
};

const ActivityTimeline = ({ data, isLoading: parentLoading, compact = false }) => {
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState({});
  const [selectedActivity, setSelectedActivity] = useState(null);
  
  // Obtener información del usuario para filtrar por rol
  const { user } = useUserAuth();
  const isTeacher = user?.role === 'profesor';
  
  // Estados para filtros
  const [timeFilter, setTimeFilter] = useState('all'); // 'all', 'today', 'yesterday', 'week'
  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all', 'payment', 'event', 'user'
  
  // Modal de detalles de pago (patrón exacto de Billing)
  const { isOpen: isDetailModalOpen, onOpen: onDetailModalOpen, onClose: onDetailModalClose } = useDisclosure();
  // Modal de detalles de actividad
  const { isOpen: isActivityModalOpen, onOpen: onActivityModalOpen, onClose: onActivityModalClose } = useDisclosure();
  const toast = useToast();
  const history = useHistory();

  // Abrir modal de detalles de pago (patrón exacto de Billing)
  const openDetailModal = (payment) => {
    setSelectedPayment(payment);
    // Configurar customer siguiendo patrón de Billing
    setSelectedCustomer({
      first_name: payment.billing?.first_name || '',
      last_name: payment.billing?.last_name || ''
    });
    onDetailModalOpen();
  };

  // Abrir modal de detalles de actividad
  const openActivityModal = (activity) => {
    console.log('🔍 TIMELINE: Abriendo modal de detalles para actividad:', activity.type, activity.id);
    setSelectedActivity(activity);
    onActivityModalOpen();
  };

  // Función para filtrar actividades por tiempo
  const filterByTime = (activities, filter) => {
    if (filter === 'all') return activities;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    return activities.filter(activity => {
      const activityDate = new Date(activity.timestamp);
      
      switch (filter) {
        case 'today':
          return activityDate >= today;
        case 'yesterday':
          return activityDate >= yesterday && activityDate < today;
        case 'week':
          return activityDate >= weekAgo;
        default:
          return true;
      }
    });
  };

  // Función para filtrar actividades por categoría
  const filterByCategory = (activities, filter) => {
    if (filter === 'all') return activities;
    return activities.filter(activity => activity.type === filter);
  };

  // Función para filtrar actividades por rol (omitir pagos si es profesor)
  const filterByRole = (activities) => {
    if (isTeacher) {
      return activities.filter(activity => activity.type !== 'payment');
    }
    return activities;
  };

  // Aplicar filtros a las actividades
  const getFilteredActivities = (activities) => {
    let filtered = filterByTime(activities, timeFilter);
    filtered = filterByCategory(filtered, categoryFilter);
    filtered = filterByRole(filtered); // Filtrar por rol (omitir pagos para profesores)
    return filtered;
  };

  // Cargar actividades del timeline unificado
  useEffect(() => {
    const loadActivities = async () => {
      if (!data?.recentActivity) return;
      
      try {
        setIsLoading(true);
        
        // Combinar actividades de datos básicos con timeline unificado
        const unifiedTimeline = await getUnifiedActivityTimeline();
        
        setActivities(unifiedTimeline || data.recentActivity || []);
        
      } catch (error) {
        console.error('❌ ACTIVITY TIMELINE: Error:', error);
        // Fallback a datos básicos
        setActivities(data.recentActivity || []);
      } finally {
        setIsLoading(false);
      }
    };

    loadActivities();
  }, [data]);

  // Aplicar filtros a las actividades
  const filteredActivities = getFilteredActivities(activities);
  const displayActivities = showAll ? filteredActivities : filteredActivities.slice(0, 6);
  const hasMore = filteredActivities.length > 6;
  

  if (parentLoading || isLoading) {
    return (
      <Fade in={true}>
        <VStack spacing={4}>
          <HStack spacing={3} justify="center" py={8}>
            <Box position="relative">
              <Spinner 
                size="xl" 
                color="blue.500" 
                thickness="4px"
                speed="0.8s"
              />
              <Box
                position="absolute"
                top="-4px"
                left="-4px"
                width="56px"
                height="56px"
                border="3px solid"
                borderColor="blue.200"
                borderRadius="50%"
                opacity="0.4"
                animation={`${pulseGlow} 1.5s ease-in-out infinite`}
              />
            </Box>
            <VStack spacing={2} ml={4}>
              <Text color="blue.600" fontWeight="semibold" fontSize="lg">
                Cargando actividad reciente...
              </Text>
              <Text color="gray.500" fontSize="sm">
                Obteniendo las últimas actualizaciones
              </Text>
            </VStack>
          </HStack>
          {[1, 2, 3].map(i => (
            <SlideFade key={i} in={true} offsetY="20px" delay={i * 0.1}>
              <Skeleton 
                height="120px" 
                width="100%" 
                borderRadius="xl"
                startColor={useColorModeValue('gray.100', 'gray.700')}
                endColor={useColorModeValue('gray.200', 'gray.600')}
              />
            </SlideFade>
          ))}
        </VStack>
      </Fade>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <SlideFade in={true} offsetY="20px">
        <VStack spacing={6} textAlign="center" py={12}>
          <ScaleFade in={true} initialScale={0.8}>
            <Box
              animation={`${pulseGlow} 3s ease-in-out infinite`}
            >
              <Icon as={FiClock} boxSize={16} color="gray.300" />
            </Box>
          </ScaleFade>
          <VStack spacing={2}>
            <Text fontSize="xl" color="gray.600" fontWeight="semibold">
              Sin actividad reciente
            </Text>
            <Text fontSize="md" color="gray.500">
              Las nuevas actividades aparecerán aquí automáticamente
            </Text>
          </VStack>
          <ScaleFade in={true} delay={0.3}>
            <Box
              w="full"
              h="2px"
              bg="linear-gradient(90deg, transparent, rgba(66, 153, 225, 0.3), transparent)"
              borderRadius="full"
            />
          </ScaleFade>
        </VStack>
      </SlideFade>
    );
  }

  return (
    <>
      <VStack spacing={6} align="stretch" w={'full'} className="custom-scrollbar">
        {/* Resumen de actividad */}
        {!compact && <ActivitySummary activities={filteredActivities} isTeacher={isTeacher} />}
        
        {!compact && <Divider />}
        
        {/* Timeline de actividades */}
        <VStack spacing={3} align="stretch">
          {!compact && (
            <HStack justify="space-between">
              <Text fontSize="md" fontWeight="semibold" color="gray.700">
                Últimas Actividades
              </Text>
              <Text fontSize="sm" color="gray.500">
                {filteredActivities.length} de {activities.length} actividades
              </Text>
            </HStack>
          )}

          {/* Filtros compactos */}
          {!compact && (
            <HStack spacing={3} flexWrap="wrap">
              <Select
                size="sm"
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                width="auto"
                minW="120px"
              >
                <option value="all">Todas</option>
                <option value="today">Hoy</option>
                <option value="yesterday">Ayer</option>
                <option value="week">Semana pasada</option>
              </Select>

              <Select
                size="sm"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                width="auto"
                minW="100px"
              >
                <option value="all">Todas</option>
                {!isTeacher && <option value="payment">Pagos</option>}
                <option value="event">Agenda</option>
                <option value="user">Usuarios</option>
                <option value="survey">Formularios</option>
              </Select>

              {(timeFilter !== 'all' || categoryFilter !== 'all') && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setTimeFilter('all');
                    setCategoryFilter('all');
                  }}
                  fontSize="xs"
                >
                  Limpiar
                </Button>
              )}
            </HStack>
          )}

          {displayActivities.length > 0 ? (
            compact ? (
              <VStack spacing={0} align="stretch">
                {displayActivities.map((item, index) => (
                  <CompactActivityRow
                    key={item.id || index}
                    item={item}
                    onClick={() => openActivityModal(item)}
                  />
                ))}
              </VStack>
            ) : (
              <Fade in={displayActivities.length > 0}>
                <VStack spacing={3} align="stretch">
                  {displayActivities.map((item, index) => (
                    <TimelineItem
                      key={item.id || index}
                      item={item}
                      onPaymentClick={openDetailModal}
                      onActivityClick={openActivityModal}
                      index={index}
                    />
                  ))}
                </VStack>
              </Fade>
            )
          ) : (
            <SlideFade in={true} offsetY="10px">
              <Box textAlign="center" py={8}>
                <ScaleFade in={true}>
                  <Icon as={FiClock} boxSize={12} color="gray.300" mb={4} />
                </ScaleFade>
                <VStack spacing={2}>
                  <Text fontSize="md" color="gray.600" fontWeight="semibold">
                    Sin resultados
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    No hay actividades que coincidan con los filtros
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    Intenta cambiar los filtros para ver más resultados
                  </Text>
                </VStack>
              </Box>
            </SlideFade>
          )}
          
          {hasMore && (
            <Button
              variant="ghost"
              colorScheme="blue"
              onClick={() => setShowAll(!showAll)}
              leftIcon={<FiMoreHorizontal />}
              size="sm"
            >
              {showAll ? 'Ver menos' : `Ver todas (${filteredActivities.length - 6} más)`}
            </Button>
          )}
        </VStack>

        {/* Nota informativa */}
        {!compact && (
          <Box mt={4} p={3} bg="blue.50" borderRadius="md">
            <Text fontSize="xs" color="blue.700" textAlign="center">
              💡 Las acciones se ejecutan en tiempo real y actualizan automáticamente el dashboard
            </Text>
          </Box>
        )}
      </VStack>

      {/* Modal de detalles de pago (importado exactamente de Billing/Profile) */}
      {selectedPayment && (
        <ModalOrderEdit 
          isOpen={isDetailModalOpen} 
          onClose={onDetailModalClose} 
          order={selectedPayment} 
          action={'view'} 
          customer={selectedCustomer}
        />
      )}

      {/* Modal de detalles de actividad con navegación a perfil */}
      <ModalActivityDetail
        activity={selectedActivity}
        isOpen={isActivityModalOpen}
        onClose={onActivityModalClose}
      />
    </>
  );
};

export default ActivityTimeline;